// apps/web/src/views/app-views/leads/index.js

import {
  Button,
  Card,
  Descriptions,
  Divider,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'

import {
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons'

import {
  useEffect,
  useState,
} from 'react'

import LeadFormService from 'services/LeadFormService'

const {
  Title,
  Text,
  Paragraph,
} = Typography

// ======================================================
// HELPERS
// ======================================================

const formatDateTime = (
  value,
) => {
  if (!value) {
    return '-'
  }

  try {
    return new Date(
      value,
    ).toLocaleString(
      'pt-BR',
    )
  } catch (error) {
    return '-'
  }
}

const getAttemptStatus = (
  status,
) => {
  switch (status) {
    case 'COMPLETED':
      return {
        text: 'Finalizado',
        color: 'green',
      }

    case 'IN_PROGRESS':
      return {
        text: 'Em andamento',
        color: 'orange',
      }

    case 'CANCELLED':
      return {
        text: 'Cancelado',
        color: 'red',
      }

    default:
      return {
        text:
          status ||
          'Desconhecido',

        color:
          'default',
      }
  }
}

// ======================================================
// PÁGINA
// ======================================================

const LeadsPage = () => {
  // ====================================================
  // ESTADOS
  // ====================================================

  const [
    leads,
    setLeads,
  ] = useState([])

  const [
    forms,
    setForms,
  ] = useState([])

  const [
    formId,
    setFormId,
  ] = useState()

  const [
    loading,
    setLoading,
  ] = useState(false)

  // ====================================================
  // RESULTADO DO QUIZ
  // ====================================================

  const [
    resultModalOpen,
    setResultModalOpen,
  ] = useState(false)

  const [
    selectedLead,
    setSelectedLead,
  ] = useState(null)

  const [
    selectedAttempt,
    setSelectedAttempt,
  ] = useState(null)

  // ====================================================
  // CARREGAR FORMULÁRIOS
  // ====================================================

  const loadForms =
    async () => {
      try {
        const data =
          await LeadFormService.list()

        setForms(
          Array.isArray(data)
            ? data
            : [],
        )
      } catch (error) {
        console.error(
          '[LEADS] Erro ao carregar formulários:',
          error,
        )

        message.error(
          'Erro ao carregar formulários',
        )
      }
    }

  // ====================================================
  // CARREGAR LEADS
  // ====================================================

  const loadLeads =
    async (
      selectedFormId =
        formId,
    ) => {
      setLoading(
        true,
      )

      try {
        const data =
          await LeadFormService
            .listLeads(
              selectedFormId,
            )

        setLeads(
          Array.isArray(data)
            ? data
            : [],
        )
      } catch (error) {
        console.error(
          '[LEADS] Erro ao carregar leads:',
          error,
        )

        message.error(
          'Erro ao carregar leads',
        )
      } finally {
        setLoading(
          false,
        )
      }
    }

  // ====================================================
  // INICIALIZAÇÃO
  // ====================================================

  useEffect(
    () => {
      loadForms()
      loadLeads()

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [],
  )

  // ====================================================
  // VALOR DE CAMPO PERSONALIZADO
  // ====================================================

  const getFieldValue = (
    lead,
    key,
  ) => {
    if (
      !lead ||
      !Array.isArray(
        lead.values,
      )
    ) {
      return '-'
    }

    const item =
      lead.values.find(
        (value) =>
          value
            ?.field
            ?.fieldKey ===
          key,
      )

    return (
      item?.value ||
      '-'
    )
  }

  // ====================================================
  // NOME DO PARTICIPANTE
  // ====================================================

  const getLeadName = (
    lead,
  ) => {
    if (!lead) {
      return '-'
    }

    const directName =
      lead.nome

    if (directName) {
      return directName
    }

    const dynamicName =
      getFieldValue(
        lead,
        'nome',
      )

    if (
      dynamicName &&
      dynamicName !== '-'
    ) {
      return dynamicName
    }

    const participantName =
      lead
        ?.quizAttempts
        ?.[0]
        ?.participant
        ?.name

    return (
      participantName ||
      '-'
    )
  }

  // ====================================================
  // TENTATIVAS DO QUIZ
  // ====================================================

  const getQuizAttempts = (
    lead,
  ) => {
    if (
      !Array.isArray(
        lead?.quizAttempts,
      )
    ) {
      return []
    }

    return lead.quizAttempts
  }

  const getPreferredAttempt = (
    lead,
  ) => {
    const attempts =
      getQuizAttempts(
        lead,
      )

    if (
      attempts.length ===
      0
    ) {
      return null
    }

    const completed =
      attempts.find(
        (attempt) =>
          attempt.status ===
          'COMPLETED',
      )

    return (
      completed ||
      attempts[0]
    )
  }

  // ====================================================
  // ABRIR RESULTADO
  // ====================================================

  const openQuizResult = (
    lead,
    attempt,
  ) => {
    if (
      !lead ||
      !attempt
    ) {
      return
    }

    setSelectedLead(
      lead,
    )

    setSelectedAttempt(
      attempt,
    )

    setResultModalOpen(
      true,
    )
  }

  // ====================================================
  // FECHAR RESULTADO
  // ====================================================

  const closeQuizResult =
    () => {
      setResultModalOpen(
        false,
      )

      setSelectedLead(
        null,
      )

      setSelectedAttempt(
        null,
      )
    }

  // ====================================================
  // COLUNAS PRINCIPAIS
  // ====================================================

  const columns = [
    {
      title:
        'Data',

      dataIndex:
        'createdAt',

      key:
        'createdAt',

      width:
        180,

      render: (
        value,
      ) =>
        formatDateTime(
          value,
        ),
    },

    {
      title:
        'Nome',

      dataIndex:
        'nome',

      key:
        'nome',

      render: (
        value,
        record,
      ) =>
        value ||
        getFieldValue(
          record,
          'nome',
        ),
    },

    {
      title:
        'Telefone',

      dataIndex:
        'telefone',

      key:
        'telefone',

      render: (
        value,
        record,
      ) =>
        value ||
        getFieldValue(
          record,
          'telefone',
        ),
    },

    {
      title:
        'E-mail',

      dataIndex:
        'email',

      key:
        'email',

      render: (
        value,
        record,
      ) =>
        value ||
        getFieldValue(
          record,
          'email',
        ),
    },

    {
      title:
        'Formulário',

      dataIndex: [
        'form',
        'name',
      ],

      key:
        'form',

      render: (
        value,
      ) =>
        value ||
        '-',
    },

    {
      title:
        'Evento',

      dataIndex:
        'eventId',

      key:
        'eventId',

      render: (
        value,
      ) =>
        value ||
        '-',
    },

    {
      title:
        'Game',

      dataIndex:
        'gameId',

      key:
        'gameId',

      render: (
        value,
      ) =>
        value ||
        '-',
    },

    {
      title:
        'LGPD',

      dataIndex:
        'consentLgpd',

      key:
        'consentLgpd',

      width:
        100,

      align:
        'center',

      render: (
        value,
      ) =>
        value ? (
          <Tag color="green">
            Sim
          </Tag>
        ) : (
          <Tag color="red">
            Não
          </Tag>
        ),
    },

    // ==================================================
    // RESULTADO DO QUIZ
    // ==================================================

    {
      title:
        'Resultado',

      key:
        'quizResult',

      width:
        170,

      align:
        'center',

      render: (
        _,
        record,
      ) => {
        const attempts =
          getQuizAttempts(
            record,
          )

        if (
          attempts.length ===
          0
        ) {
          return (
            <Text
              type="secondary"
            >
              -
            </Text>
          )
        }

        const attempt =
          getPreferredAttempt(
            record,
          )

        if (!attempt) {
          return '-'
        }

        return (
          <Button
            type="primary"
            size="small"
            icon={
              <EyeOutlined />
            }
            onClick={() =>
              openQuizResult(
                record,
                attempt,
              )
            }
          >
            Ver resultado
          </Button>
        )
      },
    },
  ]

  // ====================================================
  // COLUNAS DOS CAMPOS DO LEAD
  // ====================================================

  const leadValueColumns = [
    {
      title:
        'Campo',

      key:
        'field',

      render: (
        _,
        row,
      ) =>
        row
          ?.field
          ?.label ||
        row
          ?.field
          ?.fieldKey ||
        '-',
    },

    {
      title:
        'Valor',

      dataIndex:
        'value',

      key:
        'value',

      render: (
        value,
      ) =>
        value ||
        '-',
    },
  ]

  // ====================================================
  // COLUNAS DAS RESPOSTAS DO QUIZ
  // ====================================================

  const answerColumns = [
    {
      title:
        '#',

      key:
        'number',

      width:
        55,

      align:
        'center',

      render: (
        _,
        __,
        index,
      ) =>
        index +
        1,
    },

    {
      title:
        'Pergunta',

      key:
        'question',

      render: (
        _,
        answer,
      ) =>
        answer
          ?.question
          ?.title ||
        answer
          ?.question
          ?.text ||
        answer
          ?.question
          ?.question ||
        '-',
    },

    {
      title:
        'Resposta',

      key:
        'answer',

      render: (
        _,
        answer,
      ) =>
        answer
          ?.option
          ?.text ||
        answer
          ?.option
          ?.label ||
        answer
          ?.option
          ?.title ||
        '-',
    },

    {
      title:
        'Pontos',

      dataIndex:
        'points',

      key:
        'points',

      width:
        90,

      align:
        'center',

      render: (
        value,
      ) => (
        <Tag color="blue">
          {value ?? 0}
        </Tag>
      ),
    },
  ]

  // ====================================================
  // STATUS SELECIONADO
  // ====================================================

  const selectedStatus =
    getAttemptStatus(
      selectedAttempt
        ?.status,
    )

  // ====================================================
  // RENDER
  // ====================================================

  return (
    <>
      {/* ================================================= */}
      {/* CABEÇALHO */}
      {/* ================================================= */}

      <Space
        style={{
          width:
            '100%',

          justifyContent:
            'space-between',

          marginBottom:
            16,
        }}
      >
        <div>
          <Title
            level={2}
            style={{
              margin:
                0,
            }}
          >
            Leads
          </Title>

          <p>
            Contatos captados antes dos games.
          </p>
        </div>

        <Button
          icon={
            <ReloadOutlined />
          }
          loading={
            loading
          }
          onClick={() =>
            loadLeads()
          }
        >
          Atualizar
        </Button>
      </Space>

      {/* ================================================= */}
      {/* FILTRO */}
      {/* ================================================= */}

      <Card
        style={{
          marginBottom:
            16,
        }}
      >
        <Select
          allowClear
          showSearch
          style={{
            width:
              420,

            maxWidth:
              '100%',
          }}
          placeholder="Filtrar por formulário"
          value={
            formId
          }
          optionFilterProp="children"
          onChange={(
            value,
          ) => {
            setFormId(
              value,
            )

            loadLeads(
              value,
            )
          }}
        >
          {forms.map(
            (form) => (
              <Select.Option
                key={
                  form.id
                }
                value={
                  form.id
                }
              >
                {form.name}
              </Select.Option>
            ),
          )}
        </Select>
      </Card>

      {/* ================================================= */}
      {/* TABELA DE LEADS */}
      {/* ================================================= */}

      <Card>
        <Table
          rowKey="id"
          loading={
            loading
          }
          dataSource={
            leads
          }
          columns={
            columns
          }
          scroll={{
            x:
              1250,
          }}
          pagination={{
            pageSize:
              20,

            showSizeChanger:
              true,

            showTotal: (
              total,
            ) =>
              `${total} lead${
                total === 1
                  ? ''
                  : 's'
              }`,
          }}
          expandable={{
            expandedRowRender:
              (
                record,
              ) => (
                <Table
                  size="small"
                  rowKey="id"
                  pagination={
                    false
                  }
                  dataSource={
                    record.values ||
                    []
                  }
                  columns={
                    leadValueColumns
                  }
                />
              ),
          }}
        />
      </Card>

      {/* ================================================= */}
      {/* MODAL - RESULTADO DO QUIZ */}
      {/* ================================================= */}

      <Modal
        title="Resultado do Quiz"
        open={
          resultModalOpen
        }
        onCancel={
          closeQuizResult
        }
        footer={[
          <Button
            key="close"
            onClick={
              closeQuizResult
            }
          >
            Fechar
          </Button>,
        ]}
        width={
          900
        }
        centered
        destroyOnClose
      >
        {selectedAttempt ? (
          <>
            {/* =========================================== */}
            {/* PARTICIPANTE */}
            {/* =========================================== */}

            <Descriptions
              bordered
              column={{
                xs: 1,
                sm: 1,
                md: 2,
              }}
              size="small"
            >
              <Descriptions.Item
                label="Participante"
              >
                <Text strong>
                  {getLeadName(
                    selectedLead,
                  )}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item
                label="Status"
              >
                <Tag
                  color={
                    selectedStatus
                      .color
                  }
                >
                  {
                    selectedStatus
                      .text
                  }
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item
                label="Pontuação"
              >
                <Text strong>
                  {selectedAttempt
                    .totalScore ??
                    0}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item
                label="Perguntas respondidas"
              >
                {selectedAttempt
                  .answeredQuestions ??
                  0}
              </Descriptions.Item>

              <Descriptions.Item
                label="Iniciado em"
              >
                {formatDateTime(
                  selectedAttempt
                    .startedAt,
                )}
              </Descriptions.Item>

              <Descriptions.Item
                label="Finalizado em"
              >
                {formatDateTime(
                  selectedAttempt
                    .finishedAt,
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* =========================================== */}
            {/* FAIXA / PERFIL DO RESULTADO */}
            {/* =========================================== */}

            <Divider />

            <Title
              level={4}
              style={{
                marginTop:
                  0,
              }}
            >
              Resultado
            </Title>

            {selectedAttempt
              ?.resultRange ? (
              <Card
                size="small"
                style={{
                  marginBottom:
                    20,
                }}
              >
                <Title
                  level={4}
                  style={{
                    marginTop:
                      0,

                    marginBottom:
                      8,
                  }}
                >
                  {selectedAttempt
                    .resultRange
                    .title ||
                    'Resultado'}
                </Title>

                {selectedAttempt
                  .resultRange
                  .subtitle && (
                  <Text
                    type="secondary"
                  >
                    {
                      selectedAttempt
                        .resultRange
                        .subtitle
                    }
                  </Text>
                )}

                {selectedAttempt
                  .resultRange
                  .description && (
                  <Paragraph
                    style={{
                      marginTop:
                        16,

                      marginBottom:
                        0,
                    }}
                  >
                    {
                      selectedAttempt
                        .resultRange
                        .description
                    }
                  </Paragraph>
                )}

                {selectedAttempt
                  .resultRange
                  .recommendation && (
                  <>
                    <Divider />

                    <Text strong>
                      Recomendação
                    </Text>

                    <Paragraph
                      style={{
                        marginTop:
                          8,

                        marginBottom:
                          0,
                      }}
                    >
                      {
                        selectedAttempt
                          .resultRange
                          .recommendation
                      }
                    </Paragraph>
                  </>
                )}

                {selectedAttempt
                  .resultRange
                  .minScore !==
                  undefined &&
                  selectedAttempt
                    .resultRange
                    .maxScore !==
                    undefined && (
                    <>
                      <Divider />

                      <Text
                        type="secondary"
                      >
                        Faixa de pontuação:{' '}
                        {
                          selectedAttempt
                            .resultRange
                            .minScore
                        }{' '}
                        a{' '}
                        {
                          selectedAttempt
                            .resultRange
                            .maxScore
                        }
                      </Text>
                    </>
                  )}
              </Card>
            ) : (
              <Text
                type="secondary"
              >
                Nenhuma faixa de resultado associada.
              </Text>
            )}

            {/* =========================================== */}
            {/* RESPOSTAS */}
            {/* =========================================== */}

            <Divider />

            <Title
              level={4}
              style={{
                marginTop:
                  0,
              }}
            >
              Respostas do participante
            </Title>

            {Array.isArray(
              selectedAttempt
                .answers,
            ) &&
            selectedAttempt
              .answers
              .length >
              0 ? (
              <Table
                rowKey="id"
                size="small"
                pagination={
                  false
                }
                dataSource={
                  selectedAttempt
                    .answers
                }
                columns={
                  answerColumns
                }
                scroll={{
                  x:
                    650,
                }}
              />
            ) : (
              <Empty
                description="Nenhuma resposta registrada"
              />
            )}

            {/* =========================================== */}
            {/* OUTRAS TENTATIVAS */}
            {/* =========================================== */}

            {getQuizAttempts(
              selectedLead,
            ).length >
              1 && (
              <>
                <Divider />

                <Title
                  level={4}
                >
                  Participações deste lead
                </Title>

                <Table
                  size="small"
                  rowKey="id"
                  pagination={
                    false
                  }
                  dataSource={
                    getQuizAttempts(
                      selectedLead,
                    )
                  }
                  columns={[
                    {
                      title:
                        'Data',

                      key:
                        'date',

                      render: (
                        _,
                        attempt,
                      ) =>
                        formatDateTime(
                          attempt
                            .startedAt ||
                            attempt
                              .createdAt,
                        ),
                    },

                    {
                      title:
                        'Status',

                      key:
                        'status',

                      width:
                        130,

                      render: (
                        _,
                        attempt,
                      ) => {
                        const status =
                          getAttemptStatus(
                            attempt
                              .status,
                          )

                        return (
                          <Tag
                            color={
                              status.color
                            }
                          >
                            {
                              status.text
                            }
                          </Tag>
                        )
                      },
                    },

                    {
                      title:
                        'Pontos',

                      dataIndex:
                        'totalScore',

                      key:
                        'totalScore',

                      width:
                        90,

                      align:
                        'center',

                      render: (
                        value,
                      ) =>
                        value ??
                        0,
                    },

                    {
                      title:
                        'Resultado',

                      key:
                        'result',

                      render: (
                        _,
                        attempt,
                      ) =>
                        attempt
                          ?.resultRange
                          ?.title ||
                        '-',
                    },

                    {
                      title:
                        'Ação',

                      key:
                        'action',

                      width:
                        100,

                      align:
                        'center',

                      render: (
                        _,
                        attempt,
                      ) => (
                        <Button
                          size="small"
                          type={
                            selectedAttempt
                              ?.id ===
                            attempt.id
                              ? 'primary'
                              : 'default'
                          }
                          icon={
                            <EyeOutlined />
                          }
                          onClick={() =>
                            setSelectedAttempt(
                              attempt,
                            )
                          }
                        >
                          Ver
                        </Button>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </>
        ) : (
          <Empty
            description="Resultado não encontrado"
          />
        )}
      </Modal>
    </>
  )
}

export default LeadsPage